import React from 'react'
import { arrayOf, bool, number, oneOfType, shape, string } from 'prop-types'
import { css } from '@emotion/core'
import { HeaderBlock, useAtSize, PersonBlock } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import DOMPurify from 'dompurify'
import getRelationshipStringKeyFromAscendancyNumber from '../../../../../shared/helpers/relationship_helper'
import { extractFourDigitYear } from '../../../../../shared/helpers/factDate_Helper'

export default function Event({ gender, thumbnailIcon, person, year, showPersonBlock, events }) {
  const [t] = useTranslation()
  const atSize = useAtSize()

  const mainStyle = css`
    display: flex;
    justify-content: center;
    flex-direction: ${atSize({ default: 'column', md: 'row' })};
  `
  const eventListContainerStyle = css`
    width: 100%;
    display: flex;
    justify-content: center;
    flex-direction: column;
  `
  const headlineStyle = (twoOrMoreEvents) => css`
    font-size: ${atSize({ default: '16px', md: '20px', lg: '24px' })};
    ${twoOrMoreEvents && 'display: list-item;'};
    ${twoOrMoreEvents && 'list-style-type: circle;'};
  `

  const subheaderCss = css`
    font-weight: 700;
    font-size: 14px;
  `

  const birthYear = extractFourDigitYear(person?.dob)
  const age = year - birthYear
  const strType = age > 0 ? 'yearTurnedAge' : 'yearBorn'
  const maxW = showPersonBlock ? '400px' : '700px'

  function TextAdditional({ story }) {
    const pureText = DOMPurify.sanitize(story.textAdditional)
    // eslint-disable-next-line react/no-danger
    return <span css={subheaderCss} dangerouslySetInnerHTML={{ __html: pureText }} />
  }

  return (
    <div css={mainStyle}>
      <div css={{ width: '100%', maxWidth: maxW }}>
        {showPersonBlock && (
          <div css={{ padding: atSize({ default: '10px', lg: '20px' }) }}>
            <PersonBlock
              size={atSize({ default: 'md', lg: 'lg', xl: 'xl' })}
              avatarProps={{
                sex: gender,
                src: thumbnailIcon,
              }}
              name={`${year}`}
              details={person?.atn && t(getRelationshipStringKeyFromAscendancyNumber(person?.atn, strType), { age })}
            />
          </div>
        )}
        <div css={eventListContainerStyle}>
          {events?.length > 0 &&
            events.map((story) => (
              <div key={story.id} css={{ marginBottom: atSize({ default: '10px', lg: '20px' }) }}>
                <div
                  css={headlineStyle(events?.length > 1)}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(story.textHeadline),
                  }}
                />
                {story?.textAdditional && <TextAdditional story={story} />}
              </div>
            ))}
        </div>
        {!events && (
          <HeaderBlock
            heading={t('aam.no.data.for.year', "We don't have data for the selected year")}
            size="md"
            centered
          />
        )}
      </div>
    </div>
  )
}

Event.propTypes = {
  gender: string,
  thumbnailIcon: string,
  person: shape({
    active: bool,
    atn: oneOfType([number, string]),
    displayName: string,
    dob: oneOfType([number, string]),
    dod: string,
    gender: string,
    genderType: string,
    lifespan: string,
    living: bool,
    name: string,
    personId: string,
    pob: string,
    pod: string,
    portraitImageLink: string,
    portraitThumbnailLink: string,
    surname: string,
  }),
  year: number,
  showPersonBlock: bool,
  events: arrayOf(
    shape({
      complete: bool,
      error: bool,
      load: bool,
      pending: bool,
      res: arrayOf(
        shape({
          category: string,
          categoryId: number,
          endYear: number,
          icon: string,
          id: number,
          image: string,
          languageOnly: string,
          localeOnly: string,
          startYear: number,
          subcategory: string,
          textHeadline: string,
          textTitle: string,
          tileType: string,
          year: string,
        })
      ),
    })
  ),
}
